/**
 * Tira de reels.
 *
 * Carga de los videos, en tres escalones para que esten listos sin bajar megas
 * que nadie va a ver:
 *
 * 1. Precalentar: cuando la tarjeta esta a pantalla y media, los reels que caben
 *    en la tira (y los de una tira mas a la derecha) piden la lista de calidades
 *    y el primer tramo (`preload="metadata"`).
 * 2. Al asomarse un reel empieza a bajar en serio (`preload="auto"`).
 * 3. A media pantalla arranca; al salir se pausa.
 *
 * La calidad: del HLS maestro se toma la variante cuyo ancho cubre el cuadro a
 * densidad 1,5 como mucho. Un reel de 238px pide 720p y uno de 164px pide 480p,
 * en vez de dejar que el navegador suba a 1080p. Si el navegador no reproduce
 * HLS (Firefox), va el mp4. Si una fuente falla, se prueba la siguiente.
 *
 * La portada (`.rit-com__poster`) tapa el video hasta el evento `playing`.
 *
 * Con ahorro de datos o `prefers-reduced-motion` no se precarga ni se reproduce
 * nada solo: tocar el reel lo arranca. Tocar tambien sirve cuando el telefono
 * bloquea el autoplay (iPhone en ahorro de bateria).
 *
 * En escritorio la tira se arrastra con el raton; el clic se cancela si hubo
 * arrastre, para no abrir un enlace sin querer.
 */
const DENSIDAD_MAXIMA = 1.5;

/** @type {WeakMap<HTMLVideoElement, Promise<void>>} */
const preparados = new WeakMap();

/**
 * Variante del HLS maestro que cubre el ancho del cuadro. null si el maestro no
 * se puede leer; false si la variante no responde, que es el HLS roto de algunos
 * videos viejos de Shopify (el maestro existe y sus listas dan 404): ahi se va
 * directo al mp4 en vez de esperar dos errores.
 * @param {string} maestro
 * @param {number} ancho
 * @returns {Promise<string | null | false>}
 */
async function varianteAjustada(maestro, ancho) {
  try {
    const base = new URL(maestro, location.href);
    const respuesta = await fetch(base);
    if (!respuesta.ok) return null;
    const lineas = (await respuesta.text()).split('\n');
    const variantes = [];
    lineas.forEach((linea, i) => {
      const resolucion = linea.match(/RESOLUTION=(\d+)x\d+/);
      const uri = lineas[i + 1]?.trim();
      if (resolucion && uri && !uri.startsWith('#')) {
        variantes.push({ ancho: Number(resolucion[1]), url: new URL(uri, base).href });
      }
    });
    if (!variantes.length) return null;
    variantes.sort((a, b) => a.ancho - b.ancho);
    const objetivo = ancho * Math.min(window.devicePixelRatio || 1, DENSIDAD_MAXIMA);
    const elegida = (variantes.find((v) => v.ancho >= objetivo) ?? variantes.at(-1)).url;
    const prueba = await fetch(elegida);
    return prueba.ok ? elegida : false;
  } catch {
    return null;
  }
}

/**
 * Fuentes a probar en orden.
 * @param {HTMLVideoElement} video
 */
async function fuentes(video) {
  const { hls, mp4 } = video.dataset;
  const lista = [];
  if (hls && video.canPlayType('application/vnd.apple.mpegurl')) {
    const variante = await varianteAjustada(hls, video.clientWidth || 240);
    if (variante) lista.push(variante);
    if (variante !== false) lista.push(hls);
  }
  if (mp4) lista.push(mp4);
  return lista;
}

/**
 * Asigna la fuente una sola vez; las llamadas siguientes solo suben el `preload`.
 * @param {HTMLVideoElement} video
 * @param {'metadata' | 'auto'} carga
 */
function preparar(video, carga) {
  if (carga === 'auto' || video.preload === 'none') video.preload = carga;
  if (preparados.has(video)) return preparados.get(video);

  const listo = fuentes(video).then((lista) => {
    if (!lista.length) return;
    let intento = 0;
    video.addEventListener('error', () => {
      intento += 1;
      if (intento < lista.length) video.src = lista[intento];
    });
    video.src = lista[0];
  });
  preparados.set(video, listo);
  return listo;
}

class RitReels extends HTMLElement {
  #pista = null;
  #cerca = null;
  #lateral = null;
  #visible = null;
  /** @type {Set<HTMLVideoElement>} */
  #enPantalla = new Set();
  #arrastrando = false;
  #movido = false;
  #inicioX = 0;
  #inicioScroll = 0;

  connectedCallback() {
    this.#pista = this.querySelector('[data-reels-track]');
    if (!this.#pista) return;

    const videos = [...this.#pista.querySelectorAll('video')];
    for (const video of videos) {
      video.addEventListener('playing', () => video.closest('.rit-com__reel')?.classList.add('is-reproduciendo'));
    }

    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ahorro = navigator.connection?.saveData === true;
    if (!quieto && !ahorro && videos.length) {
      this.#cerca = new IntersectionObserver(this.#alAcercarse, { rootMargin: '150% 0px' });
      this.#lateral = new IntersectionObserver(this.#alAcercarseDeLado, {
        root: this.#pista,
        rootMargin: '0px 100%',
      });
      this.#visible = new IntersectionObserver(this.#alAsomarse, { threshold: [0, 0.5] });
      this.#cerca.observe(this);
      for (const video of videos) this.#visible.observe(video);
    }

    this.#pista.addEventListener('pointerdown', this.#alPresionar);
    window.addEventListener('pointermove', this.#alMover);
    window.addEventListener('pointerup', this.#alSoltar);
    this.#pista.addEventListener('click', this.#alHacerClic, true);
  }

  disconnectedCallback() {
    this.#cerca?.disconnect();
    this.#lateral?.disconnect();
    this.#visible?.disconnect();
    window.removeEventListener('pointermove', this.#alMover);
    window.removeEventListener('pointerup', this.#alSoltar);
  }

  /* Escalon 1. El observador lateral mide contra la tira, no contra la ventana:
     si se encendiera al cargar, precalentaria aunque la seccion este lejos. */
  #alAcercarse = (entradas) => {
    if (!entradas.some((entrada) => entrada.isIntersecting)) return;
    this.#cerca.disconnect();
    for (const video of this.#pista.querySelectorAll('video')) this.#lateral.observe(video);
  };

  #alAcercarseDeLado = (entradas) => {
    for (const entrada of entradas) {
      if (!entrada.isIntersecting) continue;
      this.#lateral.unobserve(entrada.target);
      preparar(entrada.target, 'metadata');
    }
  };

  /* Escalones 2 y 3. */
  #alAsomarse = (entradas) => {
    for (const entrada of entradas) {
      const video = entrada.target;

      if (!entrada.isIntersecting) {
        this.#enPantalla.delete(video);
        video.pause();
        continue;
      }

      const listo = preparar(video, 'auto');
      if (entrada.intersectionRatio >= 0.5) {
        this.#enPantalla.add(video);
        listo.then(() => {
          if (this.#enPantalla.has(video)) video.play()?.catch(() => {});
        });
      }
    }
  };

  #alPresionar = (evento) => {
    if (evento.pointerType === 'touch') return;
    this.#arrastrando = true;
    this.#movido = false;
    this.#inicioX = evento.clientX;
    this.#inicioScroll = this.#pista.scrollLeft;
    this.#pista.dataset.dragging = 'true';
  };

  #alMover = (evento) => {
    if (!this.#arrastrando) return;
    const avance = evento.clientX - this.#inicioX;
    if (Math.abs(avance) > 4) this.#movido = true;
    this.#pista.scrollLeft = this.#inicioScroll - avance;
  };

  #alSoltar = () => {
    if (!this.#arrastrando) return;
    this.#arrastrando = false;
    this.#pista.dataset.dragging = 'false';
  };

  #alHacerClic = (evento) => {
    if (this.#movido) {
      evento.preventDefault();
      this.#movido = false;
      return;
    }

    /* Un reel con enlace abre Instagram; uno sin enlace se reproduce o se pausa. */
    const reel = evento.target.closest('.rit-com__reel');
    if (!reel || reel.matches('a')) return;
    const video = reel.querySelector('video');
    if (!video) return;

    if (video.paused) {
      preparar(video, 'auto').then(() => video.play()?.catch(() => {}));
    } else {
      video.pause();
    }
  };
}

if (!customElements.get('rit-reels')) customElements.define('rit-reels', RitReels);
